import React, { useState, useMemo } from "react";
import "../../screens/Admin/Analytics/Analytics.css"; // Reuse admin styles
import { SidebarCol } from "../../screens/chatMain/Sidebar/Sidebar.jsx";
import { MainHeader } from "../../screens/chatMain/MainHeader/MainHeader.jsx";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { 
  Box, 
  Alert, 
  ButtonGroup,
  Button
} from "@mui/material";
import { isAdminUser } from "../../utils/userUtils";

// Import platform-specific hooks 
import useAssessmentUrlFilters from "../../hooks/assessment/useAssessmentUrlFilters";
import useAssessmentAverage from "../../hooks/assessment/useAssessmentAverage";
import useAssessmentAiSummary from "../../hooks/assessment/useAssessmentAiSummary";

// Import date utilities
import {
    getCurrentMonthRange,
    getRecentMonthOptions,
    getValidatedMonthRange,
    monthKeyToLabel,
} from "../../utils/dateUtils";

// Import presentational components 
import DateControls from "../../components/common/date-controls/DateControls";
import FiltersPanel from "../../components/pearli/FiltersPanel";
import MonthRangeSelector from "../../components/pearli/MonthRangeSelector";
import RadarChart from "../../components/pearli/RadarChart";
import ScoreSummary from "../../components/pearli/ScoreSummary";
import AISummary from "../../components/pearli/AISummary";
import ParticipantStatsCard from "../../components/pearli/ParticipantStatsCard";
import PearliMonthlyTrends from "../../components/pearli/trends/PearliMonthlyTrends";

// Month Range shows the last six completed months unless the admin picks another range.
const TREND_MONTHS = 6;

export const AssessmentAnalytics = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector((s) => s.auth?.user);
    const showAdminButtons = isAdminUser(user);

    // Platform-specific URL filters hook (isolated from Pearli)
    const { params, setParams, viewMode, filters, setFilter, clearFilters } =
        useAssessmentUrlFilters();

    const [filtersOpen, setFiltersOpen] = useState(false);

    // Get current month range for "current" mode
    const { start: currentStart, end: currentEnd } = getCurrentMonthRange();
    
    const monthOptions = useMemo(() => getRecentMonthOptions(), []);
    const rawStartKey = params.get("range_start_month");
    const rawEndKey = params.get("range_end_month");
    const { startKey, endKey } = useMemo(
        () =>
            getValidatedMonthRange({
                startKey: rawStartKey,
                endKey: rawEndKey,
                defaultMonths: TREND_MONTHS,
            }),
        [rawEndKey, rawStartKey]
    );

    // CURRENT MODE: Fetch current month averages
    const {
        metrics: currentMetrics,
        data: currentData,
        loading: loadingCurrent,
        error: errorCurrent,
        hasData: hasCurrentData,
    } = useAssessmentAverage(viewMode, filters, currentStart, currentEnd);

    // FETCH AI SUMMARIES for current mode
    const { 
        summaries: aiDomainSummaries, 
        generalSummary: aiGeneralSummary, 
        loading: loadingAiSummary 
    } = useAssessmentAiSummary(viewMode, currentMetrics, loadingCurrent);

    // RANGE MODE: monthly trends for the company, with the same filters
    const trendParams = useMemo(() => {
        const p = { start_month: startKey, end_month: endKey };
        Object.entries(filters).forEach(([k, v]) => {
            if (v) p[k] = v;
        });
        return p;
    }, [startKey, endKey, filters]);

    // DISPLAY DATE RANGE STRING
    const displayDateRange = useMemo(() => {
        if (viewMode === "current") {
            return `${currentStart} → ${currentEnd}`;
        }
        return `${monthKeyToLabel(startKey)} – ${monthKeyToLabel(endKey)}`;
    }, [viewMode, startKey, endKey, currentStart, currentEnd]);

    // LOADING & ERROR STATES (range mode reports its own)
    const loading = viewMode === "current" ? loadingCurrent : false;
    const error = viewMode === "current" ? errorCurrent : null;

    // Participant counts (support legacy and admin metric keys)
    const filteredCount =
        viewMode === "current"
            ? Number(currentMetrics?.filtered_users ?? currentMetrics?.filtered_takers ?? 0)
            : 0;
    const totalCount =
        viewMode === "current"
            ? Number(currentMetrics?.total_users ?? currentMetrics?.total_all_takers ?? 0)
            : null;
    const hasFilteredData = filteredCount > 0;

    // DEBUG LOGGING
    console.log("[AssessmentAnalytics] DEBUG:", {
        viewMode,
        loading,
        error,
        hasCurrentData,
        currentMetrics,
        currentStart,
        currentEnd,
    });

    // RENDER: ADMIN LAYOUT + PEARLI-STYLE CONTENT
    return (
        <div className="admin-page">
            {/* ADMIN SIDEBAR */}
            <div className="sidebar">
                <SidebarCol />
            </div>

            {/* MAIN CONTENT */}
            <div className="admin-container-wrapper">
                {/* ADMIN HEADER */}
                <div className="header">
                    <MainHeader
                        page={"admin"}
                        titleText={"Assessment Analytics"}
                        subtitleText={"Platform Assessment Metrics and Insights"}
                    />
                </div>

                {/* MOBILE WARNING */}
                <div className="admin-content-container-mobile">
                    <div className="admin-content-section">
                        <div className="admin-section-group-container">
                            <div className="admin-section-container">
                                <div className="admin-section-header-container">
                                    <div className="admin-section-header">
                                        <div className="admin-section-text-container">
                                            <div className="admin-section-title">
                                                Analytics Unavailable on Mobile
                                            </div>
                                            <div className="admin-section-subtitle">
                                                We're sorry, but the Analytics page is currently not
                                                supported on mobile devices. For the best experience and
                                                full functionality, please access this page from a
                                                desktop or laptop computer.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DESKTOP CONTENT */}
                <div className="admin-content-container" style={{ overflowY: "auto" }}>
                    <Box sx={{ p: 2, width: "100%" }}>
                        {/* NAVIGATION BUTTONS */}
                        {showAdminButtons && (
                            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                                <ButtonGroup aria-label="admin-analytics-buttons" sx={{ gap: 1 }}>
                                    <Button
                                        variant={
                                            location.pathname === "/admin/analytics" ? "contained" : "outlined"
                                        }
                                        onClick={() => navigate("/admin/analytics")}
                                    >
                                        Analytics
                                    </Button>
                                    <Button
                                        variant={
                                            location.pathname === "/admin/assessment-analytics"
                                                ? "contained"
                                                : "outlined"
                                        }
                                        onClick={() => navigate("/admin/assessment-analytics")}
                                    >
                                        Pearli Analytics
                                    </Button>
                                </ButtonGroup>
                            </Box>
                        )}

                        {/* DATE CONTROLS (Reused from Pearli) */}
                        <DateControls
                            title="Assessment Analytics"
                            subtitle="Platform Assessment Metrics and Insights"
                            viewMode={viewMode}
                            displayDateRange={displayDateRange}
                            onChangeView={(v) => {
                                const p = new URLSearchParams(params);
                                p.set("view", v);
                                setParams(p);
                            }}
                            onToggleFilters={() => setFiltersOpen(!filtersOpen)}
                        />

                        {/* FILTERS PANEL */}
                        {filtersOpen && (
                            <FiltersPanel
                                values={filters}
                                onChange={setFilter}
                                onClear={clearFilters}
                            />
                        )}

                        {/* RANGE MODE: Month selector */}
                        {viewMode === "range" && (
                            <MonthRangeSelector
                                startMonth={startKey}
                                endMonth={endKey}
                                monthOptions={monthOptions}
                                sx={{ display: "flex", justifyContent: "flex-end" }}
                                onChangeStart={(v) => {
                                    const p = new URLSearchParams(params);
                                    p.set("range_start_month", v);
                                    setParams(p);
                                }}
                                onChangeEnd={(v) => {
                                    const p = new URLSearchParams(params);
                                    p.set("range_end_month", v);
                                    setParams(p);
                                }}
                            />
                        )}

                        {/* CURRENT MODE: Display current metrics */}
                        {viewMode === "current" ? (
                            <>
                                {/* PARTICIPANT STATS CARD */}
                                <ParticipantStatsCard
                                    totalUsers={totalCount}
                                    filteredUsers={filteredCount}
                                    loading={loadingCurrent}
                                    hasData={hasFilteredData}
                                />

                                {/* RADAR CHART */}
                                <RadarChart
                                    scores={currentMetrics || {}}
                                    loading={loadingCurrent}
                                    hasData={hasCurrentData}
                                />

                                {/* SCORE SUMMARY */}
                                <ScoreSummary
                                    scores={currentMetrics || {}}
                                    summaries={aiDomainSummaries}
                                    loading={loadingCurrent || loadingAiSummary}
                                    hasData={hasCurrentData}
                                />

                                {/* AI SUMMARY */}
                                <AISummary
                                    summary={aiGeneralSummary || null}
                                    isRefreshing={loadingCurrent || loadingAiSummary}
                                    hasData={hasCurrentData}
                                />
                            </>
                        ) : (
                            /* RANGE MODE: monthly trends and the AI overview */
                            <PearliMonthlyTrends
                                audience="employer"
                                endpoint="/api/admin/assessment-trends"
                                params={trendParams}
                            />
                        )}

                        {/* ERROR HANDLING */}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default AssessmentAnalytics;
